import { ChangeDetectorRef, Component, computed, effect, input, output, ChangeDetectionStrategy } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { FileDefinition } from "../../models/fileDefinition";

@Component({ standalone:false,
    selector: 'app-edit-kvplist',
    templateUrl: './edit-kvplist.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./edit-kvplist.component.scss'],
  })
  export class EditKVPListComponent {
    readonly kvpJson = input.required<string>();
    readonly context = input<string>("Add Key-Value Pair");
    //if set (parameter keySource 'fd'), keys are selected from the file definition's fields instead of being entered as free text
    readonly fileDefinition = input<FileDefinition | undefined>();

    readonly keyOptions = computed(() => (this.fileDefinition()?.fieldDefinitions ?? []).map(fd => fd.name).sort((a, b) => a.localeCompare(b)));

    public addButtonLabel:string = "Add Key-Value Pair";
    public keyLabel:string = "Key";
    public valueLabel:string = "Value";

    readonly onValueChanged = output<{valid:boolean, value:string, formDirty:boolean}>();

    public kvpForm:FormGroup;

    private kvpFormComponentsValueChangesSubscription?:Subscription;
    private populated = false;

    constructor(private cd:ChangeDetectorRef){
        this.kvpForm = new FormGroup({
                components: new FormArray([])
            }
        );
        effect(()=>{
            if(this.context() && this.context()==="httpHeaders"){
                this.addButtonLabel = "Add Header";
                this.keyLabel = "Name";
                this.valueLabel = "Value";
            }
            else if(this.context() && this.context()==="auth_data"){
                this.addButtonLabel = "Add authorization data";
                this.keyLabel = "Name";
                this.valueLabel = "Value";
            }
        });
        effect(()=>{
            //file definition changed (e.g. user selected another one) => keys need to be checked against the new fields
            this.keyOptions();
            if(this.populated) this.revalidateKeys(true);
        });
    }

    ngOnInit(){
    }

    ngOnDestroy(){
        if(this.kvpFormComponentsValueChangesSubscription) this.kvpFormComponentsValueChangesSubscription.unsubscribe();
    }

    ngAfterViewInit(){
        this.populateForm();
        this.populated = true;
        this.revalidateKeys(false);
        this.cd.detectChanges();
        this.kvpFormComponentsValueChangesSubscription = this.kvpForm.controls['components'].valueChanges.subscribe(value=>this.componentsChanged());
        //keys not matching the file definition (e.g. it changed since the list was saved) must block saving right away
        if(this.hasKeyErrors) queueMicrotask(()=>this.emitValue(false));
    }

    get formArr() {
        return this.kvpForm.get("components") as FormArray;
    }

    formGroup(index: number): FormGroup {
        return this.formArr.at(index) as FormGroup;
    }

    get formValueAsJson(): string {
        const components = this.kvpForm.get('components') as FormArray;
        const kvpArray = (components?.value as { key: string; value: string }[] ?? [])
            .map(kvp => ({
            key: kvp.key?.trim() ?? '',
            value: kvp.value?.trim() ?? ''
            }));
        return JSON.stringify(kvpArray);
    }

    public isStaleKey(key: string | null | undefined): boolean {
        return !!key && this.keyOptions().length > 0 && !this.keyOptions().includes(key);
    }

    private get hasKeyErrors(): boolean {
        return this.formArr.controls.some(c => c.get('key')!.hasError('notInFileDefinition') || c.get('key')!.hasError('duplicateKey'));
    }

    private componentsChanged(){
        //a changed key can create or resolve a duplicate in any other row
        this.formArr.controls.forEach(c => {
            const key = c.get('key')!;
            key.updateValueAndValidity({emitEvent:false});
            if(key.hasError('duplicateKey')) key.markAsTouched(); //flag both rows of a duplicate, not only the one being edited
        });
        this.kvpForm.updateValueAndValidity();
        this.emitValue(true);
    }

    private emitValue(formDirty:boolean){
        this.onValueChanged.emit({valid:this.kvpForm.valid, value:this.formValueAsJson, formDirty:formDirty});
    }

    private revalidateKeys(emit:boolean){
        this.formArr.controls.forEach(c => {
            const key = c.get('key')!;
            key.updateValueAndValidity({emitEvent:false});
            //show the error right away, the user has to pick another field
            if(key.hasError('notInFileDefinition') || key.hasError('duplicateKey')) key.markAsTouched();
        });
        this.kvpForm.updateValueAndValidity({emitEvent:false});
        if(emit) this.emitValue(false);
    }

    private keyValidator = (control: AbstractControl): ValidationErrors | null => {
        const key = (control.value as string | null)?.trim();
        if(!key) return null;
        if(this.isStaleKey(key)) return {notInFileDefinition:true};
        const siblings = (control.parent?.parent as FormArray | undefined)?.controls ?? [];
        const duplicate = siblings.some(s => s.get('key') !== control && (s.get('key')!.value as string | null)?.trim().toLowerCase() === key.toLowerCase());
        return duplicate ? {duplicateKey:true} : null;
    };

    private populateForm(){
        if(this.kvpJson()){
            try{
                let kvpArray:{key:string,value:string}[] = JSON.parse(this.kvpJson()!);
                if(kvpArray && kvpArray.length>0){
                    kvpArray.forEach(kvp=>{
                        this.addComponent(kvp.key,kvp.value);
                    });
                }
            }
            catch{
                //ignore error
            }
        }
    }

    public onAddComponentClick(){
        this.addComponent(undefined,undefined);
    }

    //#region components
    public addComponent(key:string|undefined, value:string|undefined) {
        if(this.kvpForm){
            const fa = this.kvpForm.get('components') as FormArray;
            let component=this.createComponent(key, value);
            fa.push(component);
        }
    }

    public createComponent(key:string|undefined, value:string|undefined): FormGroup {
        let fbGroup =  new FormGroup({
            key: new FormControl<any>(key, [Validators.required, this.keyValidator]),
            value:new FormControl<any>(value, Validators.required)
        });
        if(!value){
            //if no value make sure error is shown to user right away
            fbGroup.controls['value'].markAsTouched();
        }
        return fbGroup;
    }

    public deleteComponent(componentIndex:number) {
        const components = this.kvpForm.get('components') as FormArray;
        components.removeAt(componentIndex);
        components.markAsTouched();
    }

    public deleteAllComponents() {
        const components = this.kvpForm.get('components') as FormArray;
        components.clear();
    }
    //#endregion
  }
